import React from 'react';
import { Create, useForm } from '@refinedev/antd';
import { useNavigation } from '@refinedev/core';
import { Form, Input, InputNumber, Switch, Select, message } from 'antd';

const ACTION_OPTIONS = [
  { value: 'BODY_CHECK', label: 'Body check (scale / measure)' },
  { value: 'WATER', label: 'Water' },
  { value: 'FOOD_BREAKFAST', label: 'Food · Breakfast' },
  { value: 'FOOD_LUNCH', label: 'Food · Lunch' },
  { value: 'FOOD_DINNER', label: 'Food · Dinner' },
  { value: 'FOOD_SNACK', label: 'Food · Snack' },
  { value: 'HOME', label: 'Home' },
];

type FormValues = {
  code: string;
  hour: number;
  minute: number;
  actionType: string;
  title: string;
  body: string;
  titleZhHk?: string;
  bodyZhHk?: string;
  titleZhCn?: string;
  bodyZhCn?: string;
  isEnabled?: boolean;
  sortOrder?: number;
};

function toApiPayload(values: FormValues) {
  const translations: Record<string, { title?: string; body?: string }> = {};
  if (values.titleZhHk || values.bodyZhHk) {
    translations['zh-HK'] = {
      title: values.titleZhHk || undefined,
      body: values.bodyZhHk || undefined,
    };
  }
  if (values.titleZhCn || values.bodyZhCn) {
    translations['zh-CN'] = {
      title: values.titleZhCn || undefined,
      body: values.bodyZhCn || undefined,
    };
  }
  return {
    code: values.code,
    hour: values.hour,
    minute: values.minute ?? 0,
    actionType: values.actionType,
    title: values.title,
    body: values.body,
    translations: Object.keys(translations).length ? translations : undefined,
    isEnabled: values.isEnabled ?? true,
    sortOrder: values.sortOrder ?? 0,
  };
}

export const NotificationReminderCreate: React.FC = () => {
  const { formProps, onFinish } = useForm({ resource: 'notification-reminders' });
  const { push } = useNavigation();

  const handleSubmit = async (values: FormValues) => {
    try {
      await onFinish(toApiPayload(values));
      message.success('Reminder created');
      push('/notification-reminders');
    } catch (err: any) {
      message.error(err?.message || 'Failed to create reminder');
    }
  };

  return (
    <Create
      resource="notification-reminders"
      saveButtonProps={{ onClick: () => formProps.form?.submit() }}
    >
      <Form
        {...formProps}
        layout="vertical"
        onFinish={(values) => handleSubmit(values as FormValues)}
      >
        <Form.Item
          label="Code"
          name="code"
          rules={[{ required: true, message: 'Stable code required' }]}
        >
          <Input placeholder="e.g. breakfast" />
        </Form.Item>
        <Form.Item label="Hour (0-23)" name="hour" rules={[{ required: true }]} initialValue={8}>
          <InputNumber min={0} max={23} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Minute (0-59)" name="minute" initialValue={0}>
          <InputNumber min={0} max={59} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="Action"
          name="actionType"
          rules={[{ required: true }]}
          initialValue="FOOD_BREAKFAST"
        >
          <Select options={ACTION_OPTIONS} />
        </Form.Item>
        <Form.Item label="Title (EN)" name="title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Body (EN)" name="body" rules={[{ required: true }]}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item label="Title (zh-HK)" name="titleZhHk">
          <Input />
        </Form.Item>
        <Form.Item label="Body (zh-HK)" name="bodyZhHk">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item label="Title (zh-CN)" name="titleZhCn">
          <Input />
        </Form.Item>
        <Form.Item label="Body (zh-CN)" name="bodyZhCn">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item label="Enabled" name="isEnabled" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
        <Form.Item label="Sort Order" name="sortOrder" initialValue={0}>
          <InputNumber style={{ width: '100%' }} min={0} />
        </Form.Item>
      </Form>
    </Create>
  );
};
